import React, { useEffect, useState } from "react";
import { Mic, MicOff, PhoneOff, Users } from "lucide-react";
import { Button } from "../ui";
import { formatDuration } from "./callFormat";

export interface ActiveCallBarProps {
  callerLabel: string;
  startedAt: number;
  muted: boolean;
  familyPath: string | null;
  onToggleMute: () => void;
  onHangUp: () => void;
  onOpenFamily: (path: string) => void;
}

/** The live call, pinned to the bottom of the screen on every page. */
const ActiveCallBar: React.FC<ActiveCallBarProps> = ({
  callerLabel,
  startedAt,
  muted,
  familyPath,
  onToggleMute,
  onHangUp,
  onOpenFamily,
}) => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);
  const elapsed = Math.max(0, Math.floor((now - startedAt) / 1000));

  return (
    <div
      role="region"
      aria-label="Current call"
      className="fixed bottom-0 inset-x-0 z-[70] border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-[0_-4px_12px_rgba(0,0,0,0.08)] px-4 py-3"
    >
      <div className="mx-auto max-w-5xl flex flex-wrap items-center gap-3">
        <span className="inline-block h-2.5 w-2.5 rounded-full bg-green-500" aria-hidden="true" />
        <span className="font-semibold text-gray-900 dark:text-white truncate">
          On a call with {callerLabel}
        </span>
        <span className="text-sm tabular-nums text-gray-600 dark:text-gray-300" aria-label="Call length">
          {formatDuration(elapsed)}
        </span>
        <div className="ml-auto flex flex-wrap gap-2">
          {familyPath && (
            <Button
              variant="ghost"
              size="sm"
              icon={<Users className="w-4 h-4" />}
              onClick={() => onOpenFamily(familyPath)}
            >
              Family
            </Button>
          )}
          <Button
            variant="secondary"
            size="sm"
            aria-pressed={muted}
            icon={muted ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
            onClick={onToggleMute}
          >
            {muted ? "Unmute" : "Mute"}
          </Button>
          <Button variant="danger" size="sm" icon={<PhoneOff className="w-4 h-4" />} onClick={onHangUp}>
            Hang up
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ActiveCallBar;
